import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  IGetCallAccessTokenRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/GetCallAccessToken/types/IGetCallAccessTokenRequestInterfaces'
import {
  IGetActiveCallRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/GetActiveCall/types/IGetActiveCallRequestInterfaces'
import {
  ICreateCallInvitesRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/CreateCallInvites/types/ICreateCallInvitesRequestInterfaces'
import {
  IMuteCallParticipantRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/MuteCallParticipant/types/IMuteCallParticipantRequestInterfaces'
import {
  IRemoveCallParticipantRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/RemoveCallParticipant/types/IRemoveCallParticipantRequestInterfaces'
import {
  ISetCallAgentEnabledRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/SetCallAgentEnabled/types/ISetCallAgentEnabledRequestInterfaces'
import {
  ISetCallRecordingRequestInterfaces,
} from '@toolify/server/src/controllers/CallController/validators/SetCallRecording/types/ISetCallRecordingRequestInterfaces'
import {mapCallResponseInstance} from '@toolify/client/src/modules/api/singletons/mapCallResponseInstance'
import {
  IConversationTypeInterfacesMap,
} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'

export const useCallApiStore = defineStore('callApiStore', {
  actions: {
    async getCallAccessToken(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
    ): Promise<IGetCallAccessTokenRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {accessToken, call} = await apiRequestStore.post<IGetCallAccessTokenRequestInterfaces>(`/conversation-type/${conversationType}/call`, {
        data: {
          conversationTypePayload,
        },
      })
      return {
        accessToken,
        call: mapCallResponseInstance(call),
      }
    },
    async getActiveCall(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
    ): Promise<IGetActiveCallRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {call} = await apiRequestStore.post<IGetActiveCallRequestInterfaces>(`/conversation-type/${conversationType}/call/active`, {
        data: {
          conversationTypePayload,
        },
      }, {retryOnNetworkError: true})
      return {
        call: call ? mapCallResponseInstance(call) : null,
      }
    },
    async createCallInvites(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      userIds: string[],
    ): Promise<ICreateCallInvitesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<ICreateCallInvitesRequestInterfaces>(`/conversation-type/${conversationType}/call/invite`, {
        data: {
          conversationTypePayload,
          userIds,
        },
      })
    },
    async muteCallParticipant(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      targetIdentity: string,
      trackSid: string,
      muted: boolean,
    ): Promise<IMuteCallParticipantRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IMuteCallParticipantRequestInterfaces>(`/conversation-type/${conversationType}/call/mute`, {
        data: {
          conversationTypePayload,
          targetIdentity,
          trackSid,
          muted,
        },
      })
    },
    async removeCallParticipant(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      targetIdentity: string,
    ): Promise<IRemoveCallParticipantRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IRemoveCallParticipantRequestInterfaces>(`/conversation-type/${conversationType}/call/remove-participant`, {
        data: {
          conversationTypePayload,
          targetIdentity,
        },
      })
    },
    async setCallRecording(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      isRecording: boolean,
    ): Promise<ISetCallRecordingRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<ISetCallRecordingRequestInterfaces>(`/conversation-type/${conversationType}/call/recording`, {
        data: {
          conversationTypePayload,
          isRecording,
        },
      })
    },
    async setCallAgentEnabled(
      conversationType: ConversationType,
      conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
      isEnabled: boolean,
    ): Promise<ISetCallAgentEnabledRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<ISetCallAgentEnabledRequestInterfaces>(`/conversation-type/${conversationType}/call/agent`, {
        data: {
          conversationTypePayload,
          isEnabled,
        },
      })
    },
  },
})
